import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { MdSearch } from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import PrintActionsHeader from '../../components/shared/PrintActionsHeader';
import TableRowIconActions from '../../components/shared/TableRowIconActions';
import { formatSerialDisplay } from '../admin/members/memberData';
import TablePagination from '../../components/shared/TablePagination';
import {
  ColumnFilterHeader,
  formatPhoneFilterValue,
  formatDateFilterValue,
  uniqueColumnOptions,
  useColumnFilters,
} from '../../components/admin/ColumnFilterHeader';
import { usePanelToast } from '../../context/PanelToastContext';
import {
  fetchMembershipsPage,
  updateMembershipSale,
  deleteMembership,
  fetchPackages,
} from '../../services/receptionist/receptionistApi';
import {
  exportMembershipCsv,
  exportMembershipExcel,
  exportMembershipPdf,
  exportMemberships,
  printMembershipList,
} from './exportMemberships';
import { dedupeMemberships, dedupeByName } from '../../utils/dedupeList';
import { PAYMENTS_UPDATE_EVENT } from './paymentStore';
import { RECEPTIONIST_BRANCH_EVENT } from '../../services/gym/branchContext';
import { formatCurrency } from './receptionistData';
import {
  SaleMethodColumnHeaders,
  SaleMethodCells,
  SALE_METHOD_COLUMN_COUNT,
  saleMethodFilterDefaults,
} from './sales/SaleMethodTableColumns';
import PayBalanceModal from './sales/PayBalanceModal';
import RemainingClearHistory from './sales/RemainingClearHistory';
import SaleReceiptModal from './sales/SaleReceiptModal';
import SplitPaymentFields from './sales/SplitPaymentFields';
import { buildReceiptFromPaymentRow } from './sales/buildSaleReceipt';
import { membershipCanPayBalance, membershipTotalAmount } from '../../utils/membershipFees';
import {
  membershipStartDisplay,
  membershipExpiryDisplay,
  membershipDurationDisplay,
  membershipDateDisplay,
  membershipPaymentNoteDisplay,
} from '../../utils/membershipDates';
import { MEMBERSHIP_DURATION_FILTER_OPTIONS } from './sales/saleDurationUtils';
import { pkTodayRange } from '../../utils/pkTime';
import { membershipToPaymentRow, membershipRowForReceipt } from './sales/salePaymentUtils';
import {
  defaultPaymentSplits,
  sumPaymentSplits,
} from '../../utils/splitPaymentUtils';
import '../owner/ownerPages.css';
import '../owner/subscriptions/subscriptions.css';
import '../admin/memberships/memberships.css';
import '../admin/adminListPages.css';
import './members/members.css';
import './receptionistPanel.css';
import './sales.css';
import './sales/ancillarySales.css';

const actionTypes = {
  edit: 'edit',
};

function getEmptyEditForm() {
  return {
    member: '',
    gymId: '',
    package: '',
    packageFee: '',
    entryDate: '',
    start: '',
    expiry: '',
    datesTbc: false,
    paymentDbId: null,
    paymentSplits: defaultPaymentSplits(),
    registrationFee: 0,
    discountAmount: 0,
    personalTrainingFee: 0,
    classesFee: 0,
  };
}

function splitsFromMembership(m) {
  if (Array.isArray(m?.paymentSplits) && m.paymentSplits.length) {
    return defaultPaymentSplits().map((row) => {
      const found = m.paymentSplits.find((s) => s.method === row.method);
      return {
        method: row.method,
        amount: found?.amount != null && found.amount !== '' ? String(found.amount) : '',
      };
    });
  }
  const paid = Number(m?.totalPaid) || 0;
  if (paid <= 0) return defaultPaymentSplits();
  return defaultPaymentSplits().map((row, index) => (
    index === 0 ? { ...row, amount: String(paid) } : row
  ));
}

function resolveSerialNo(record) {
  if (record?.serialNo != null && record.serialNo !== '') {
    return formatSerialDisplay(record.serialNo) || '—';
  }
  if (record?.gymId != null && record.gymId !== '' && record.gymId !== '—') {
    return formatSerialDisplay(record.gymId) || String(record.gymId);
  }
  return '—';
}

function getMembershipStatusLabel(membership) {
  if (membership.datesTbc) return 'TBC';
  if (membership.status === 'cancelled') return 'Cancelled';
  if (membership.frozen || membership.status === 'inactive') return 'Inactive';
  if (membership.status === 'expired' || membership.status === 'due') {
    return membership.status === 'due' ? 'Due' : 'Expired';
  }
  return 'Active';
}

function StatusBadge({ membership }) {
  if (membership.datesTbc) {
    return <span className="owner-badge owner-badge-info">TBC</span>;
  }
  if (membership.status === 'cancelled') {
    return <span className="owner-badge owner-badge-warning">Cancelled</span>;
  }
  if (membership.frozen || membership.status === 'inactive') {
    return <span className="owner-badge owner-badge-warning">Inactive</span>;
  }
  if (membership.status === 'expired' || membership.status === 'due') {
    return <span className="owner-badge owner-badge-warning">{membership.status === 'due' ? 'Due' : 'Expired'}</span>;
  }
  return <span className="owner-badge owner-badge-success">Active</span>;
}

function Memberships() {
  const toast = usePanelToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeModal, setActiveModal] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [formKey, setFormKey] = useState(0);

  const [editForm, setEditForm] = useState(getEmptyEditForm);
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const todayRange = pkTodayRange();
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 50;
  const requestIdRef = useRef(0);
  const [payTarget, setPayTarget] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const {
    filters,
    updateFilter,
    openFilterKey,
    toggleFilter,
  } = useColumnFilters({
    date: '',
    member: '',
    cnic: '',
    phone: '',
    email: '',
    gender: 'all',
    dob: '',
    trainer: '',
    package: '',
    serialNo: '',
    duration: 'all',
    start: '',
    expiry: '',
    packageFee: 'all',
    registrationFee: 'all',
    totalPaid: 'all',
    pendingAmount: 'all',
    ...saleMethodFilterDefaults(),
    paymentNote: '',
    status: 'all',
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedFilters(filters), 300);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, debouncedFilters]);

  const loadList = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    try {
      setError('');
      setLoading(true);
      const [result, packageRows] = await Promise.all([
        fetchMembershipsPage({
          page,
          pageSize,
          search: debouncedSearch,
          filters: {
            ...debouncedFilters,
            rangeStart: todayRange.startDate,
            rangeEnd: todayRange.endDate,
          },
        }),
        fetchPackages().catch(() => []),
      ]);
      if (reqId !== requestIdRef.current) return;
      const priceByPackage = new Map(
        (packageRows || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          Number(p.price) || 0,
        ]),
      );
      const durationByPackage = new Map(
        (packageRows || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          String(p.duration || '').trim(),
        ]).filter(([, duration]) => duration),
      );
      let mappedRows = dedupeMemberships((result.rows || []).map((row) => {
        const mapped = { ...row };
        // Keep saved 0 package fee — only catalog-fill when no linked payment / fee was never set
        if (
          !mapped.complimentary
          && !mapped.paymentDbId
          && !(Number(mapped.packageFee) > 0)
          && mapped.packageFee !== 0
        ) {
          const catalogPrice = priceByPackage.get(String(mapped.package || '').trim().toLowerCase()) || 0;
          if (catalogPrice > 0) mapped.packageFee = catalogPrice;
        }
        mapped.packageFee = Math.max(0, Number(mapped.packageFee) || 0);
        if (mapped.complimentary) {
          mapped.packageFee = 0;
          mapped.registrationFee = 0;
          mapped.totalAmount = 0;
          mapped.totalPaid = 0;
          mapped.pendingAmount = 0;
          mapped.discountAmount = 0;
          mapped.personalTrainingFee = 0;
          mapped.classesFee = 0;
          mapped.paymentSplits = [];
        }
        mapped.duration = membershipDurationDisplay(mapped, durationByPackage);
        return mapped;
      }));
      setList(mappedRows);
      setTotal(result.total || 0);
      setPackages(dedupeByName(packageRows || []));
    } catch (err) {
      if (reqId !== requestIdRef.current) return;
      setList([]);
      setTotal(0);
      setError(err.message || 'Failed to load memberships');
    } finally {
      if (reqId === requestIdRef.current) setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, debouncedFilters, todayRange.startDate, todayRange.endDate]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const refresh = () => {
      loadList();
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    return () => {
      window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
      window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, refresh);
    };
  }, [loadList]);

  const selected = useMemo(() => list.find((m) => m.id === selectedId), [list, selectedId]);

  const pageItems = list;

  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const nextPage = () => setPage((p) => Math.min(totalPages, p + 1));
  const prevPage = () => setPage((p) => Math.max(1, p - 1));

  const handleExport = async (exporterFn) => {
    try {
      const result = await fetchMembershipsPage({
        search: debouncedSearch,
        filters: {
          ...debouncedFilters,
          rangeStart: todayRange.startDate,
          rangeEnd: todayRange.endDate,
        },
        forExport: true,
      });
      const packageRows = await fetchPackages().catch(() => []);
      const durationByPackage = new Map(
        (packageRows || []).map((p) => [
          String(p.name || '').trim().toLowerCase(),
          String(p.duration || '').trim(),
        ]).filter(([, duration]) => duration),
      );
      const rows = dedupeMemberships(result.rows || []).map((row) => ({
        ...row,
        duration: membershipDurationDisplay(row, durationByPackage),
      }));
      exporterFn(rows);
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  const openModal = (type, id = '') => {
    setFormKey((k) => k + 1);
    setSelectedId(id);
    setActiveModal(type);
    if (type === actionTypes.edit && id) {
      const m = list.find((item) => item.id === id);
      if (m) {
        setEditForm({
          member: m.member,
          gymId: resolveSerialNo(m),
          package: m.package || '',
          packageFee: Number.isFinite(Number(m.packageFee)) ? String(Number(m.packageFee)) : '',
          entryDate: String(membershipDateDisplay(m) || '').replace(/^—$/, '').slice(0, 10),
          start: m.datesTbc ? '' : (m.start || m.startDate || ''),
          expiry: m.datesTbc ? '' : (m.expiry || ''),
          datesTbc: Boolean(m.datesTbc),
          paymentDbId: m.paymentDbId || null,
          paymentSplits: splitsFromMembership(m),
          registrationFee: Number(m.registrationFee) || 0,
          discountAmount: Number(m.discountAmount) || 0,
          personalTrainingFee: Number(m.personalTrainingFee) || 0,
          classesFee: Number(m.classesFee) || 0,
        });
      }
    }
  };

  const closeModal = () => {
    setActiveModal(null);
    setSelectedId('');
  };

  const paidPreview = useMemo(
    () => sumPaymentSplits(editForm.paymentSplits),
    [editForm.paymentSplits],
  );

  const totalDuePreview = useMemo(
    () => membershipTotalAmount(editForm),
    [editForm],
  );

  const balancePreview = useMemo(
    () => Math.max(0, Math.round((totalDuePreview - paidPreview) * 100) / 100),
    [totalDuePreview, paidPreview],
  );

  const handlePackageChange = (packageName) => {
    const pkg = packages.find(
      (p) => String(p.name || '').trim().toLowerCase() === String(packageName || '').trim().toLowerCase(),
    );
    const catalogPrice = Number(pkg?.price);
    const catalogReg = Number(pkg?.registrationFee);
    setEditForm((prev) => ({
      ...prev,
      package: packageName,
      packageFee: Number.isFinite(catalogPrice) ? String(catalogPrice) : (pkg ? '0' : prev.packageFee),
      registrationFee: Number.isFinite(catalogReg) ? catalogReg : (pkg ? 0 : prev.registrationFee),
    }));
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    if (!String(editForm.package || '').trim()) {
      toast.showError('Select or enter a package.');
      return;
    }
    const paid = sumPaymentSplits(editForm.paymentSplits);
    if (paid < 0) {
      toast.showError('Enter a valid paid amount.');
      return;
    }

    try {
      setSaving(true);
      const hasBothDates = Boolean(editForm.start && editForm.expiry);
      let datesTbc = editForm.datesTbc && !hasBothDates ? true : false;
      // Blank start (and not TBC) → today
      let start = String(editForm.start || '').slice(0, 10) || null;
      let expiry = String(editForm.expiry || '').slice(0, 10) || null;
      if (!datesTbc && !start) {
        const todayIso = new Date().toISOString().slice(0, 10);
        start = todayIso;
        datesTbc = false;
      }
      if (hasBothDates) datesTbc = false;
      const packageFee = Math.max(0, Number(editForm.packageFee) || 0);

      await updateMembershipSale(selected.id, {
        packageName: editForm.package.trim(),
        start: datesTbc ? null : start,
        expiry: datesTbc ? null : expiry,
        datesTbc,
        packageFee,
        paymentSplits: editForm.paymentSplits,
        paymentDbId: editForm.paymentDbId || selected.paymentDbId || null,
        registrationFee: editForm.registrationFee,
        discountAmount: editForm.discountAmount,
        personalTrainingFee: editForm.personalTrainingFee,
        classesFee: editForm.classesFee,
        entryDate: editForm.entryDate || '',
      });

      closeModal();
      toast.showUpdated('Membership');
      await loadList();
    } catch (err) {
      const message = err.message || 'Failed to update membership';
      setError(message);
      toast.showError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleShowReceipt = (membership) => {
    const row = membershipRowForReceipt(membership);
    if (!row) return;
    setReceipt(buildReceiptFromPaymentRow(row, { title: 'Membership', label: 'Subscription' }));
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    try {
      setDeleting(true);
      await deleteMembership(deleteTarget.id);
      setDeleteTarget(null);
      toast.showDeleted('Membership');
      await loadList();
    } catch (err) {
      const message = err.message || 'Failed to delete membership';
      setError(message);
      toast.showError(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Memberships" subtitle="Search by Serial No and manage member subscription records" variant="banner" />

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}
      {loading && <p className="owner-placeholder-text owner-mb-16">Loading memberships...</p>}

      <PrintActionsHeader
        startDate={todayRange.startDate}
        endDate={todayRange.endDate}
        datesReadOnly
        onPrint={() => handleExport(printMembershipList)}
        onCsv={() => handleExport(exportMembershipCsv)}
        onPdf={() => handleExport(exportMembershipPdf)}
        onExcel={() => handleExport(exportMembershipExcel)}
        onExport={() => handleExport(exportMemberships)}
        ariaLabel="Membership print and export"
      >
        <div className="rec-member-search-field">
          <MdSearch className="rec-member-search-icon" />
          <input
            type="text"
            className="owner-form-input rec-member-search-input"
            placeholder="Search name, serial, phone, or CNIC..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </PrintActionsHeader>
      <p className="owner-placeholder-text owner-mb-16">
        {search.trim()
          ? 'Searching all dates in this branch'
          : 'Pakistan time · today only — memberships with today’s entry / sale date'}
      </p>

      <div className="owner-card rec-panel-card rec-member-list-card">
        <div className="owner-table-wrap">
          <table className="owner-table rec-member-table rec-membership-table">
            <thead>
              <tr>
                <ColumnFilterHeader
                  label="Entry Date"
                  filterKey="date"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Serial No"
                  filterKey="serialNo"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search serial..."
                />
                <ColumnFilterHeader
                  label="Member"
                  filterKey="member"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search member..."
                />
                <ColumnFilterHeader
                  label="CNIC"
                  filterKey="cnic"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search CNIC..."
                />
                <ColumnFilterHeader
                  label="Phone"
                  filterKey="phone"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="e.g. 0300-1234567"
                  formatValue={formatPhoneFilterValue}
                />
                <ColumnFilterHeader
                  label="Email"
                  filterKey="email"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search email..."
                />
                <ColumnFilterHeader
                  label="Gender"
                  filterKey="gender"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['male', 'female', 'other']}
                  allLabel="All Genders"
                />
                <ColumnFilterHeader
                  label="DOB"
                  filterKey="dob"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Trainer"
                  filterKey="trainer"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search trainer..."
                />
                <ColumnFilterHeader
                  label="Package"
                  filterKey="package"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search package..."
                />
                <ColumnFilterHeader
                  label="Duration"
                  filterKey="duration"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={MEMBERSHIP_DURATION_FILTER_OPTIONS}
                  allLabel="All Durations"
                />
                <ColumnFilterHeader
                  label="Start Date"
                  filterKey="start"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Expiry"
                  filterKey="expiry"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="DD-MM-YYYY"
                  formatValue={formatDateFilterValue}
                />
                <ColumnFilterHeader
                  label="Reg. Fee"
                  filterKey="registrationFee"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (m) => formatCurrency(m.registrationFee))}
                  allLabel="All Reg. Fees"
                />
                <ColumnFilterHeader
                  label="Package Fee"
                  filterKey="packageFee"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (m) => formatCurrency(m.packageFee))}
                  allLabel="All Package Fees"
                />
                <ColumnFilterHeader
                  label="Amount Paid"
                  filterKey="totalPaid"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (m) => formatCurrency(m.totalPaid))}
                  allLabel="All Paid"
                />
                <SaleMethodColumnHeaders
                  sales={list}
                  filters={filters}
                  openFilterKey={openFilterKey}
                  toggleFilter={toggleFilter}
                  updateFilter={updateFilter}
                />
                <ColumnFilterHeader
                  label="Pending"
                  filterKey="pendingAmount"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={uniqueColumnOptions(list, (m) => formatCurrency(m.pendingAmount))}
                  allLabel="All Pending"
                />
                <ColumnFilterHeader
                  label="Payment Note"
                  filterKey="paymentNote"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  placeholder="Search note..."
                />
                <ColumnFilterHeader
                  label="Status"
                  filterKey="status"
                  openKey={openFilterKey}
                  onToggle={toggleFilter}
                  filters={filters}
                  onFilterChange={updateFilter}
                  options={['TBC', 'Active', 'Inactive', 'Expired', 'Due', 'Cancelled']}
                  allLabel="All Statuses"
                />
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.length > 0 ? (
                pageItems.map((m) => (
                <tr key={m.id}>
                  <td>{membershipDateDisplay(m)}</td>
                  <td className="rec-member-gym-id">{resolveSerialNo(m)}</td>
                  <td>{m.member}</td>
                  <td>{m.cnic || '—'}</td>
                  <td>{m.phone || '—'}</td>
                  <td>{m.email || '—'}</td>
                  <td>{m.gender || '—'}</td>
                  <td>{m.dob || '—'}</td>
                  <td>{m.trainer || '—'}</td>
                  <td>{m.package || '—'}</td>
                  <td>{m.duration || '—'}</td>
                  <td>{membershipStartDisplay(m)}</td>
                  <td>{membershipExpiryDisplay(m)}</td>
                  <td>{formatCurrency(m.registrationFee)}</td>
                  <td>{formatCurrency(m.packageFee)}</td>
                  <td className="finance-amount-positive">{formatCurrency(m.totalPaid)}</td>
                  <SaleMethodCells row={m} />
                  <td className={m.pendingAmount > 0 ? 'finance-amount-negative' : ''}>
                    {formatCurrency(m.pendingAmount)}
                  </td>
                  <td>{membershipPaymentNoteDisplay(m)}</td>
                  <td><StatusBadge membership={m} /></td>
                  <td>
                    <TableRowIconActions
                      onEdit={() => openModal(actionTypes.edit, m.id)}
                      onDelete={() => setDeleteTarget(m)}
                      editLabel={`Edit ${m.member}`}
                      deleteLabel={`Delete ${m.member}`}
                    >
                      {membershipCanPayBalance(m) && (
                        <button
                          type="button"
                          className="owner-btn owner-btn-primary owner-btn-sm"
                          onClick={() => setPayTarget(m)}
                        >
                          Pay pending
                        </button>
                      )}
                      <button
                        type="button"
                        className="owner-btn owner-btn-secondary owner-btn-sm"
                        onClick={() => handleShowReceipt(m)}
                      >
                        Receipt
                      </button>
                    </TableRowIconActions>
                  </td>
                </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={20 + SALE_METHOD_COLUMN_COUNT} className="owner-placeholder-text">
                    {search.trim() ? 'No memberships match this search.' : 'No memberships found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <TablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          from={from}
          to={to}
          onPageChange={setPage}
          onPrev={prevPage}
          onNext={nextPage}
        />
      </div>

      <RemainingClearHistory title="Membership — Remaining Clear" />

      {activeModal === actionTypes.edit && (
        <div className="subscription-modal-overlay" onClick={closeModal}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Edit Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={closeModal} aria-label="Close">×</button>
            </div>
            <form key={formKey} className="subscription-form" onSubmit={handleEdit}>
              <div className="membership-form-grid">
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="edit-gym-id">Serial No</label>
                  <input
                    id="edit-gym-id"
                    type="text"
                    className="owner-form-input rec-member-country-readonly"
                    value={editForm.gymId}
                    readOnly
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <label className="owner-form-label" htmlFor="edit-member">Member</label>
                  <input
                    id="edit-member"
                    type="text"
                    className="owner-form-input"
                    value={editForm.member}
                    readOnly
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-package">Package</label>
                  <input
                    id="edit-package"
                    type="text"
                    className="owner-form-input"
                    list="edit-membership-package-list"
                    value={editForm.package}
                    onChange={(e) => handlePackageChange(e.target.value)}
                    placeholder="Select or type package name"
                    required
                    autoComplete="off"
                  />
                  <datalist id="edit-membership-package-list">
                    {packages.map((pkg) => (
                      <option
                        key={pkg.id || pkg.name}
                        value={pkg.name}
                        label={Number(pkg.price) > 0 ? `${pkg.name} — ${formatCurrency(pkg.price)}` : pkg.name}
                      />
                    ))}
                  </datalist>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-package-fee">Package Fee</label>
                  <input
                    id="edit-package-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={editForm.packageFee == null ? '' : String(editForm.packageFee)}
                    onChange={(e) => setEditForm((p) => ({ ...p, packageFee: e.target.value }))}
                    placeholder="Optional — leave blank (shows as 0)"
                    disabled={selected?.complimentary}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-registration-fee">Registration Fee</label>
                  <input
                    id="edit-registration-fee"
                    type="text"
                    inputMode="decimal"
                    className="owner-form-input"
                    value={editForm.registrationFee == null ? '' : String(editForm.registrationFee)}
                    onChange={(e) => setEditForm((p) => ({ ...p, registrationFee: e.target.value }))}
                    placeholder="0"
                    disabled={selected?.complimentary}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-entry-date">Entry / Sale Date</label>
                  <input
                    id="edit-entry-date"
                    type="date"
                    className="owner-form-input"
                    value={editForm.entryDate || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, entryDate: e.target.value }))}
                  />
                  <p className="owner-placeholder-text" style={{ marginTop: 4, marginBottom: 0 }}>
                    Cash, Z Report, petty cash, and dashboard revenue use this date.
                  </p>
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-start">Membership Start</label>
                  <input
                    id="edit-start"
                    type="date"
                    className="owner-form-input"
                    value={editForm.start || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, start: e.target.value }))}
                  />
                </div>
                <div className="owner-form-group">
                  <label className="owner-form-label" htmlFor="edit-expiry">Membership End</label>
                  <input
                    id="edit-expiry"
                    type="date"
                    className="owner-form-input"
                    value={editForm.expiry || ''}
                    onChange={(e) => setEditForm((p) => ({ ...p, expiry: e.target.value }))}
                  />
                </div>
                <div className="owner-form-group membership-form-full">
                  <SplitPaymentFields
                    idPrefix="edit-membership"
                    splits={editForm.paymentSplits}
                    onChange={(paymentSplits) => setEditForm((p) => ({ ...p, paymentSplits }))}
                    disabled={saving}
                  />
                  <p className="owner-placeholder-text rec-sale-payment-hint" style={{ marginTop: 8 }}>
                    Total due: <strong>{formatCurrency(totalDuePreview)}</strong>
                    {' · '}
                    Amount paid: <strong>{formatCurrency(paidPreview)}</strong>
                    {' · '}
                    Balance: <strong>{formatCurrency(balancePreview)}</strong>
                    {!editForm.paymentDbId && (
                      <> · No payment linked yet — saving will create/update the payment record.</>
                    )}
                  </p>
                </div>
              </div>
              {editForm.datesTbc && (
                <p className="owner-placeholder-text" style={{ marginTop: 0 }}>
                  Entry / Sale Date controls financial reports. Set membership start and end when the member begins gym — saving both dates clears TBC.
                </p>
              )}
              <div className="subscription-form-actions">
                <button type="submit" className="owner-btn owner-btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </button>
                <button type="button" className="owner-btn owner-btn-secondary" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {payTarget?.paymentDbId && (
        <PayBalanceModal
          key={payTarget.paymentDbId}
          paymentRow={membershipToPaymentRow(payTarget)}
          saleMeta={{ title: 'Membership', label: 'Subscription' }}
          onClose={() => setPayTarget(null)}
          onPaid={async (receiptData) => {
            setReceipt(receiptData);
            await loadList();
          }}
        />
      )}

      {deleteTarget && (
        <div className="subscription-modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
          <div className="subscription-modal admin-form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="subscription-modal-header">
              <h2 className="subscription-modal-title">Delete Membership</h2>
              <button type="button" className="subscription-modal-close" onClick={() => setDeleteTarget(null)} aria-label="Close">×</button>
            </div>
            <p className="owner-placeholder-text" style={{ margin: '0 0 16px' }}>
              Delete membership for <strong>{deleteTarget.member}</strong>? This action cannot be undone.
            </p>
            <div className="subscription-form-actions">
              <button type="button" className="owner-btn owner-btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button type="button" className="owner-btn rec-member-btn-delete" onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SaleReceiptModal receipt={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export default Memberships;
